import { canonicalRouteSpecs } from './routeCatalog';

export type RouteAccess =
  | 'authenticated'
  | 'customer'
  | 'member-ai-v2'
  | 'member'
  | 'member-automation'
  | 'member-non-dawson'
  | 'member-or-customer';

export type AppRouterContext = Readonly<{
  auth: Readonly<{
    isAuthenticated: boolean;
    isCustomer: boolean;
    isDawsonsWarehouseUser: boolean;
    isMember: boolean;
    loading: boolean;
    roles: readonly string[];
  }>;
  featureFlags: Readonly<{ automationSettings: boolean; aiV2?: boolean }>;
  isE2E: boolean;
}>;

export type AuthStatus = 'authenticated' | 'loading' | 'signed-out';
export type FrameKey = 'authenticated' | 'single-audience' | 'dual-audience';

export const PENDING_APP_ROUTER_CONTEXT: AppRouterContext = {
  auth: {
    isAuthenticated: false,
    isCustomer: false,
    isDawsonsWarehouseUser: false,
    isMember: false,
    loading: true,
    roles: [],
  },
  featureFlags: { automationSettings: false },
  isE2E: false,
};

export function getAuthStatus(context: AppRouterContext): AuthStatus {
  if (context.auth.isAuthenticated) return 'authenticated';
  if (context.auth.loading) return 'loading';
  return 'signed-out';
}

export function isRouteDeclared(context: AppRouterContext, access: RouteAccess): boolean {
  if (access === 'authenticated') return true;
  if (access === 'customer') return context.auth.isCustomer;
  if (access === 'member-ai-v2') return context.auth.isMember && context.featureFlags.aiV2 === true;
  if (access === 'member') return context.auth.isMember;
  if (access === 'member-automation') {
    return context.auth.isMember && context.featureFlags.automationSettings;
  }
  if (access === 'member-non-dawson') {
    return context.auth.isMember && !context.auth.isDawsonsWarehouseUser;
  }
  return context.auth.isMember || context.auth.isCustomer;
}

export function resolveCollisionAudience(context: AppRouterContext): 'member' | 'customer' | undefined {
  if (context.auth.isMember) return 'member';
  if (context.auth.isCustomer) return 'customer';
  return undefined;
}

export function getFrameKey(context: AppRouterContext, access: RouteAccess): FrameKey {
  if (access === 'authenticated') return 'authenticated';
  if (access === 'customer') return 'single-audience';
  return context.auth.isCustomer && context.auth.isMember ? 'dual-audience' : 'single-audience';
}

function beforeFirst(value: string, delimiter: string): string {
  const index = value.indexOf(delimiter);
  return index === -1 ? value : value.slice(0, index);
}

export function resolveSignedOutLoginHref(
  context: AppRouterContext,
  access: RouteAccess | undefined,
  currentHref: string
): string {
  const hrefWithoutHash = beforeFirst(currentHref, '#');
  const redirectTo =
    access !== undefined && isRouteDeclared(context, access) ? hrefWithoutHash : beforeFirst(hrefWithoutHash, '?');
  return `${canonicalRouteSpecs.login.path}?${new URLSearchParams({ redirectTo }).toString()}`;
}
